import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { apiFetch } from './api';
import { supabase } from './supabaseClient';
import { queryClient } from './queryClient';
import { ToastProvider } from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import TopNav from './components/TopNav';
import ScrollToTop from './components/ScrollToTop';
import Sidebar, { SIDEBAR_WIDTH } from './components/Sidebar';
import AdminSidebar from './components/AdminSidebar';
import ImpersonationBanner, { BANNER_HEIGHT } from './components/ImpersonationBanner';
import { ImpersonationProvider, useImpersonation } from './context/ImpersonationContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Profile from './pages/Profile';
import ProfileList from './pages/ProfileList';
import ProfileForm from './pages/ProfileForm';
import Dashboard from './pages/Dashboard';
import Home from './pages/Home';
import HowItWorks from './pages/HowItWorks';
import Pricing from './pages/Pricing';
import Blog from './pages/Blog';
import HowToFindEUTenders from './pages/blog/HowToFindEUTenders';
import MistakesFirstBid from './pages/blog/MistakesFirstBid';
import GuideIndex from './pages/guide/GuideIndex';
import WhatIsATender from './pages/guide/WhatIsATender';
import WhoCanBid from './pages/guide/WhoCanBid';
import BiddingProcess from './pages/guide/BiddingProcess';
import WinningABid from './pages/guide/WinningABid';
import EURules from './pages/guide/EURules';
import Glossary from './pages/guide/Glossary';
import TenderDetail from './pages/TenderDetail';
import Onboarding from './pages/Onboarding';
import Settings from './pages/Settings';
import Billing from './pages/Billing';
import TenderSearch from './pages/TenderSearch';
import AdminOverview from './pages/admin/AdminOverview';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminIngestion from './pages/admin/AdminIngestion';
import AdminProspector from './pages/admin/AdminProspector';
import AdminTenders from './pages/admin/AdminTenders';
import AdminTenderDetail from './pages/admin/AdminTenderDetail';
import AdminPipelines from './pages/admin/AdminPipelines';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsOfService from './pages/legal/TermsOfService';
import GDPRData from './pages/legal/GDPRData';
import CookiePolicy from './pages/legal/CookiePolicy';

const APP_ROUTES = ['/dashboard', '/profile', '/profiles', '/tender', '/settings', '/billing', '/search', '/onboarding'];
const ADMIN_ROUTES = ['/admin'];

const INK = '#0A1F4A';
const ACC = '#FF7A3D';

function TrialCountdownBanner({ days }) {
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('trial_banner_dismissed') === 'true');
  if (dismissed) return null;
  const urgent = days <= 3;
  return (
    <div style={{
      background: urgent ? '#FFF3E0' : '#FFFBEB',
      borderBottom: `1px solid ${urgent ? '#FFCC80' : '#FDE68A'}`,
      padding: '10px 24px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
      fontSize: 13,
    }}>
      <span style={{ color: INK }}>
        <strong style={{ color: urgent ? ACC : '#B45309' }}>
          {days === 0 ? 'Your trial ends today' : `${days} day${days !== 1 ? 's' : ''} left in your trial`}
        </strong>
        {' — upgrade to keep your matches and email alerts.'}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
        <Link to="/billing" style={{ color: INK, fontWeight: 600, textDecoration: 'underline' }}>View plans</Link>
        <button onClick={() => { sessionStorage.setItem('trial_banner_dismissed', 'true'); setDismissed(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400E', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
      </div>
    </div>
  );
}

function TrialExpiredBanner() {
  return (
    <div style={{
      background: '#FFF3F0',
      borderBottom: `3px solid ${ACC}`,
      padding: '12px 24px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
      fontSize: 13,
    }}>
      <span style={{ color: INK }}>
        <strong style={{ color: ACC }}>Your free trial has ended.</strong>
        {' Your matches and profile are saved — upgrade to reactivate email alerts and daily refresh.'}
      </span>
      <Link to="/billing" style={{
        flexShrink: 0,
        background: ACC, color: '#fff',
        fontWeight: 600, fontSize: 13,
        padding: '8px 18px', borderRadius: 8,
        textDecoration: 'none', whiteSpace: 'nowrap',
      }}>Upgrade now</Link>
    </div>
  );
}

function AppLayout({ isAuth, isAdmin, subscriptionStatus, trialDaysRemaining, children }) {
  const { pathname } = useLocation();
  const { impersonating, stopImpersonating } = useImpersonation();

  const isAdminRoute = ADMIN_ROUTES.some((r) => pathname.startsWith(r));
  const isAppRoute = isAuth && APP_ROUTES.some((r) => pathname.startsWith(r));
  const showBanner = !!impersonating && isAppRoute;
  const sidebarTop = showBanner ? 60 + BANNER_HEIGHT : 60;

  if (isAdminRoute && isAdmin) {
    return (
      <>
        <AdminSidebar />
        <div style={{ marginLeft: SIDEBAR_WIDTH }}>
          {children}
        </div>
      </>
    );
  }

  if (isAppRoute) {
    const showTrialCountdown = subscriptionStatus === 'trialing' && trialDaysRemaining != null && trialDaysRemaining <= 7;
    const showExpired = subscriptionStatus === 'expired';
    return (
      <>
        {showBanner && (
          <ImpersonationBanner user={impersonating} onExit={stopImpersonating} />
        )}
        <Sidebar topOffset={sidebarTop} />
        <div style={{ marginLeft: SIDEBAR_WIDTH, paddingTop: showBanner ? BANNER_HEIGHT : 0 }}>
          {showExpired && <TrialExpiredBanner />}
          {!showExpired && showTrialCountdown && <TrialCountdownBanner days={trialDaysRemaining} />}
          {children}
        </div>
      </>
    );
  }

  return children;
}

export default function App() {
  const [session, setSession] = useState(null);
  const [userId, setUserId] = useState(null);
  const [email, setEmail] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [validating, setValidating] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(null);

  // Track the Supabase session (login, logout, token refresh, OAuth callback).
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (!data.session) setValidating(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      if (!newSession) {
        // Drop every cached query so a different user logging in on the same
        // tab can never see the previous user's data from in-memory cache.
        queryClient.clear();
        setUserId(null);
        setEmail(null);
        setIsAdmin(false);
        setSubscriptionStatus(null);
        setTrialDaysRemaining(null);
        setValidating(false);
      }
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  // Load profile + subscription info from our API. Returns a promise so callers
  // can await it (e.g. on first load to clear the validating spinner).
  const refreshMe = useCallback(() => {
    return apiFetch('/auth/me')
      .then((data) => {
        if (data) {
          setUserId(data.user_id);
          setEmail(data.email);
          setIsAdmin(!!data.is_admin);
          setSubscriptionStatus(data.subscription_status || null);
          setTrialDaysRemaining(data.trial_days_remaining ?? null);
        }
      })
      .catch(() => {
        /* apiFetch already handles 401 by signing out + redirecting */
      });
  }, []);

  // Whenever there's a session, (re)load subscription info. Re-runs after login
  // so subscription state is always fresh.
  //
  // Do NOT flip `validating` back to true here. Supabase fires this effect again
  // on every TOKEN_REFRESHED event — which happens each time the browser tab
  // regains focus. Showing the full-screen loader on those refreshes would
  // unmount and remount the entire app, wiping all in-page state (My Tenders
  // filters, half-typed form inputs, the active Settings tab). The initial
  // useState(true) already covers the genuine first-load gate; later refreshes
  // update auth state silently in the background.
  useEffect(() => {
    if (!session) return;
    refreshMe().finally(() => setValidating(false));
  }, [session, refreshMe]);

  // Let other parts of the app ask for a fresh subscription state without a full
  // page reload — e.g. the Dashboard fires this when returning from Stripe checkout.
  useEffect(() => {
    const onUpdate = () => refreshMe();
    window.addEventListener('tm:subscription-updated', onUpdate);
    return () => window.removeEventListener('tm:subscription-updated', onUpdate);
  }, [refreshMe]);

  // Login/Signup establish the session via Supabase directly; the listeners
  // above pick it up. Kept as a prop for those components.
  function handleLogin() {}

  async function handleLogout() {
    await supabase.auth.signOut();
    // onAuthStateChange clears the remaining state.
  }

  if (validating) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 15 }}>
        Loading
      </div>
    );
  }

  const isAuth = !!session;

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <ImpersonationProvider>
            <BrowserRouter>
            <ScrollToTop />
            <TopNav isAuth={isAuth} isAdmin={isAdmin} email={email} onLogout={handleLogout} />
            <AppLayout isAuth={isAuth} isAdmin={isAdmin} subscriptionStatus={subscriptionStatus} trialDaysRemaining={trialDaysRemaining}>
              <Routes>
                {/* Auth */}
                <Route path="/login"  element={isAuth ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />} />
                <Route path="/signup" element={isAuth ? <Navigate to="/dashboard" /> : <Signup onLogin={handleLogin} />} />

                {/* App (requires auth) */}
                <Route path="/onboarding"  element={isAuth ? <Onboarding />         : <Navigate to="/login" />} />
                <Route path="/dashboard"    element={isAuth ? <Dashboard />          : <Navigate to="/login" />} />
                <Route path="/profile"             element={isAuth ? <Profile />                    : <Navigate to="/login" />} />
                <Route path="/profiles"           element={isAuth ? <ProfileList />               : <Navigate to="/login" />} />
                <Route path="/profiles/new"       element={isAuth ? <ProfileForm />               : <Navigate to="/login" />} />
                <Route path="/profiles/:id/edit"  element={isAuth ? <ProfileForm />               : <Navigate to="/login" />} />
                <Route path="/tender/:id"   element={isAuth ? <TenderDetail />       : <Navigate to="/login" />} />
                <Route path="/settings"    element={isAuth ? <Settings />           : <Navigate to="/login" />} />
                <Route path="/billing"     element={isAuth ? <Billing />            : <Navigate to="/login" />} />
                <Route path="/search"     element={isAuth ? <TenderSearch />       : <Navigate to="/login" />} />

                {/* Admin (requires auth + is_admin) */}
                <Route path="/admin"              element={isAdmin ? <AdminOverview />   : <Navigate to="/dashboard" />} />
                <Route path="/admin/users"        element={isAdmin ? <AdminUsers />      : <Navigate to="/dashboard" />} />
                <Route path="/admin/users/:id"    element={isAdmin ? <AdminUserDetail /> : <Navigate to="/dashboard" />} />
                <Route path="/admin/ingestion"    element={isAdmin ? <AdminIngestion />     : <Navigate to="/dashboard" />} />
                <Route path="/admin/prospector"  element={isAdmin ? <AdminProspector />    : <Navigate to="/dashboard" />} />
                <Route path="/admin/tenders"     element={isAdmin ? <AdminTenders />       : <Navigate to="/dashboard" />} />
                <Route path="/admin/tenders/:id"  element={isAdmin ? <AdminTenderDetail />  : <Navigate to="/dashboard" />} />
                <Route path="/admin/pipelines"   element={isAdmin ? <AdminPipelines />    : <Navigate to="/dashboard" />} />

                {/* Tender Guide — public */}
                <Route path="/guide"                  element={<GuideIndex />} />
                <Route path="/guide/what-is-a-tender" element={<WhatIsATender />} />
                <Route path="/guide/who-can-bid"      element={<WhoCanBid />} />
                <Route path="/guide/bidding-process"  element={<BiddingProcess />} />
                <Route path="/guide/winning-a-bid"    element={<WinningABid />} />
                <Route path="/guide/eu-rules"         element={<EURules />} />
                <Route path="/guide/glossary"         element={<Glossary />} />

                {/* Marketing pages — public */}
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/pricing"   element={<Pricing />} />
                <Route path="/blog"      element={<Blog />} />
                <Route path="/blog/how-to-find-eu-tenders"  element={<HowToFindEUTenders />} />
                <Route path="/blog/5-mistakes-first-eu-bid" element={<MistakesFirstBid />} />

                {/* Legal pages — public */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms"   element={<TermsOfService />} />
                <Route path="/gdpr"    element={<GDPRData />} />
                <Route path="/cookies" element={<CookiePolicy />} />

                <Route path="/" element={<Home />} />
              </Routes>
            </AppLayout>
            </BrowserRouter>
          </ImpersonationProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
